import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'
import { IColumn } from 'core-components/library/list/types'

import {
  TMarketYoyGrowthExpectationMarket,
  TMarketYoyGrowthExpectationRtm,
} from '../../../api/types'
import { MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS } from '../../../configs/translations'
import { IMarketYoyGrowthEditTarget } from '../../../lib'
import { MarketsExpandToggle } from '../components/marketsExpandToggle/MarketsExpandToggle'
import { ScopeColumnHeader } from '../components/scopeColumnHeader/ScopeColumnHeader'
import styles from '../MarketYoyGrowthExpectationTable.module.scss'
import { TMarketYoyGrowthExpectationTableRow } from '../types'

import { createGrowthColumnPair } from './createGrowthColumn'

const KEYS = MARKET_YOY_GROWTH_EXPECTATION_TRANSLATION_KEYS

const SCOPE_HEADER_FIELD = 'scopeHeader'
const OFFERING_FIELD = 'offering'

interface IGetColumnsParams {
  isEditable: boolean
  isMarketView: boolean
  isMarketsExpanded: boolean
  market: string
  markets: TMarketYoyGrowthExpectationMarket[]
  expandedOfferings: Set<ProgramTypes>
  rtm: TMarketYoyGrowthExpectationRtm
  onToggleMarketsExpanded: () => void
  onToggleExpand: (offering: ProgramTypes) => void
  onGrowthValueCommit: (target: IMarketYoyGrowthEditTarget) => void
}

export const getColumns = ({
  isEditable,
  isMarketView,
  isMarketsExpanded,
  market,
  expandedOfferings,
  markets,
  rtm,
  onToggleExpand,
  onGrowthValueCommit,
  onToggleMarketsExpanded,
}: IGetColumnsParams): IColumn<TMarketYoyGrowthExpectationTableRow>[] => {
  const showMarketColumns = !isMarketView && isMarketsExpanded && markets.length > 0

  const offeringColumn: IColumn<TMarketYoyGrowthExpectationTableRow> = {
    field: OFFERING_FIELD,
    title: KEYS.table.offering,
    disableSort: true,
    hideSettings: true,
    titleRender: () => <></>,
  }

  const offeringContentColumn: IColumn<TMarketYoyGrowthExpectationTableRow> = {
    field: 'offeringContent',
    parentField: OFFERING_FIELD,
    title: '',
    disableSort: true,
    hideSettings: true,
    titleRender: () => (
      <Flex alignItems="center" flexDirection="row">
        <Translation text={KEYS.table.offering} span />

        {showMarketColumns ? (
          <>
            <MarketsExpandToggle
              isMarketsExpanded={isMarketsExpanded}
              title={<Translation text={KEYS.table.toWorldwide} span />}
              onToggleMarketsExpanded={onToggleMarketsExpanded}
            />
          </>
        ) : (
          <></>
        )}
      </Flex>
    ),
    renderFunction: (row) => {
      return (
        <>
          {row.type === 'group' ? (
            <div className={styles.nameRow}>
              <Button
                className={styles.expandToggle}
                iconButton={`kbase ${
                  expandedOfferings.has(row.offering) ? 'kbase-caret-down' : 'kbase-caret-right'
                }`}
                size="sm"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleExpand(row.offering)
                }}
              />
              <div className={styles.name}>
                <Translation text={row.offeringTranslationKey} />
              </div>
            </div>
          ) : (
            <div className={styles.itemName}>
              <Translation text={row.productCategory} />
            </div>
          )}
        </>
      )
    },
  }

  const columns: IColumn<TMarketYoyGrowthExpectationTableRow>[] = [
    offeringColumn,
    offeringContentColumn,
  ]

  if (showMarketColumns) {
    markets.forEach((market) => {
      columns.push(
        {
          field: `market-${market}`,
          title: market,
          disableSort: true,
          hideSettings: true,
          titleRender: () => <>{market}</>,
        },
        ...createGrowthColumnPair({
          fieldPrefix: `${market}-`,
          parentField: `market-${market}`,
          expandedOfferings,
          getValues: (row) => row.byMarket[market],
          isEditable,
          market,
          rtm,
          onGrowthValueCommit,
        })
      )
    })

    return columns
  }

  columns.push(
    {
      field: SCOPE_HEADER_FIELD,
      title: isMarketView ? market : KEYS.table.worldwide,
      disableSort: true,
      hideSettings: true,
      titleRender: () => (
        <ScopeColumnHeader
          scope={market}
          isMarketView={isMarketView}
          isMarketsExpanded={isMarketsExpanded}
          onToggleMarketsExpanded={onToggleMarketsExpanded}
        />
      ),
    },
    ...createGrowthColumnPair({
      parentField: SCOPE_HEADER_FIELD,
      expandedOfferings,
      getValues: (row) => (!isMarketView ? row.ww : row.byMarket[market]),
      isEditable,
      market: isMarketView ? market : null,
      rtm,
      onGrowthValueCommit,
    })
  )

  return columns
}
